// AI 生图 API 服务
import type {
  Conversation,
  Message,
  EffectTemplate,
  GenerateRequest,
  GenerateResponse,
  TaskStatusResponse,
  ApiResponse,
  ModelConfig,
  AiGenerateCapability,
} from '../types/aiGenerate';

// 获取对话列表
export async function getConversations(): Promise<Conversation[]> {
  const response = await fetch('/ai/conversations', {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  });
  const result: ApiResponse<Conversation[]> = await response.json();
  if (result.success) {
    return result.data || [];
  }
  throw new Error(result.message || '获取对话列表失败');
}

// 创建新对话
export async function createConversation(title?: string): Promise<Conversation> {
  const response = await fetch('/ai/conversations', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ title: title || '新对话' }),
  });
  const result: ApiResponse<Conversation> = await response.json();
  if (result.success) {
    return result.data;
  }
  throw new Error(result.message || '创建对话失败');
}

// 更新对话（重命名）
export async function updateConversation(id: string, title: string): Promise<void> {
  const response = await fetch(`/ai/conversations/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ title }),
  });
  const result: ApiResponse<void> = await response.json();
  if (!result.success) {
    throw new Error(result.message || '更新对话失败');
  }
}

// 删除对话
export async function deleteConversation(id: string): Promise<void> {
  const response = await fetch(`/ai/conversations/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  });
  const result: ApiResponse<void> = await response.json();
  if (!result.success) {
    throw new Error(result.message || '删除对话失败');
  }
}

// 获取对话消息
export async function getMessages(conversationId: string): Promise<Message[]> {
  const response = await fetch(
    `/ai/conversations/${conversationId}/messages`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    }
  );
  const result: ApiResponse<Message[]> = await response.json();
  if (result.success) {
    return result.data || [];
  }
  throw new Error(result.message || '获取消息失败');
}

// 发起生成请求
export async function generateImage(request: GenerateRequest): Promise<GenerateResponse> {
  const response = await fetch('/ai/generate', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });
  const result: ApiResponse<GenerateResponse> = await response.json();
  if (result.success) {
    return result.data;
  }
  throw new Error(result.message || '生成请求失败');
}

// 查询生成状态
export async function getTaskStatus(taskId: string): Promise<TaskStatusResponse> {
  const response = await fetch(`/ai/generate/${taskId}/status`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  });
  const result: ApiResponse<TaskStatusResponse> = await response.json();
  if (result.success) {
    return result.data;
  }
  throw new Error(result.message || '查询状态失败');
}

// 获取效果模板列表
export async function getEffectTemplates(): Promise<EffectTemplate[]> {
  const response = await fetch('/ai/templates', {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  });
  const result: ApiResponse<EffectTemplate[]> = await response.json();
  if (result.success) {
    return result.data || [];
  }
  throw new Error(result.message || '获取模板失败');
}

// 获取可用模型列表
export async function getModels(): Promise<ModelConfig[]> {
  const response = await fetch('/ai/models', {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  });
  const result: ApiResponse<ModelConfig[]> = await response.json();
  if (result.success) {
    return result.data || [];
  }
  // 如果接口失败，返回默认模型
  return [
    { id: 'gpt-image-2', name: 'GPT Image 2' },
  ];
}

export async function getCapability(): Promise<AiGenerateCapability> {
  const response = await fetch('/ai/capability', {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  const result: ApiResponse<AiGenerateCapability> = await response.json();
  if (!result.success || !result.data) {
    throw new Error(result.message || '获取 AI 生图能力失败');
  }
  return result.data;
}

// 默认生成参数
export const DEFAULT_GENERATE_PARAMS = {
  model: 'gpt-image-2',
  count: 1,
  size: '1024x1024',
  quality: 'low' as const,
};

export const SIZE_OPTIONS = [
  { value: '1024x1024', label: '方图 1024×1024' },
  { value: '1536x1024', label: '横图 1536×1024' },
  { value: '1024x1536', label: '竖图 1024×1536' },
];

export const QUALITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

// 生成数量选项
export const COUNT_OPTIONS = [1, 2, 3, 4];
